import { useCallback, useEffect, useMemo, useState } from 'react';
import { Input } from '@/design-system';
import * as dashboardApi from '@/features/dashboard/api/dashboard-api';
import { TEMPLATE_CATEGORIES, TEMPLATE_TYPES } from '@/contracts/templates';
import type { TemplateCategory, TemplateListResponse, TemplateSummary, TemplateType } from '@/contracts/templates';
import { TemplateCard } from './TemplateCard';
import { TemplatePreviewDialog } from './TemplatePreviewDialog';
import { UseTemplateDialog } from './UseTemplateDialog';
import { EditCustomTemplateDialog } from './EditCustomTemplateDialog';
import { UpdateFromFunnelDialog } from './UpdateFromFunnelDialog';
import { DeleteTemplateConfirmDialog } from './DeleteTemplateConfirmDialog';

function matchesSearch(template: TemplateSummary, query: string): boolean {
  if (!query) return true;
  const haystack = `${template.name} ${template.description} ${template.tags.join(' ')}`.toLowerCase();
  return haystack.includes(query.toLowerCase());
}

/**
 * SPEC-010 §21-25/§83 — dedicated gallery route combining Official +
 * My Templates, with search and type/category filters (§66: small V1
 * volume, client-side filtering is acceptable).
 */
export function TemplateGalleryPage() {
  const [response, setResponse] = useState<TemplateListResponse | null>(null);
  const [search, setSearch] = useState('');
  const [typeFilter, setTypeFilter] = useState<TemplateType | ''>('');
  const [categoryFilter, setCategoryFilter] = useState<TemplateCategory | ''>('');
  const [previewing, setPreviewing] = useState<TemplateSummary | null>(null);
  const [using, setUsing] = useState<TemplateSummary | null>(null);
  const [editing, setEditing] = useState<TemplateSummary | null>(null);
  const [updatingFrom, setUpdatingFrom] = useState<TemplateSummary | null>(null);
  const [deleting, setDeleting] = useState<TemplateSummary | null>(null);

  const load = useCallback(async () => {
    const res = await dashboardApi.listTemplates();
    setResponse(res);
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  const filter = useCallback(
    (items: TemplateSummary[]) =>
      items.filter(
        (item) =>
          matchesSearch(item, search) &&
          (typeFilter === '' || item.type === typeFilter) &&
          (categoryFilter === '' || item.category === categoryFilter),
      ),
    [search, typeFilter, categoryFilter],
  );

  const official = useMemo(() => filter(response?.official ?? []), [response, filter]);
  const custom = useMemo(() => filter(response?.custom ?? []), [response, filter]);

  return (
    <main className="mx-auto max-w-6xl px-6 py-8">
      <h1 className="mb-6 text-2xl font-semibold text-text-primary">Modelos</h1>

      <div className="mb-6 flex flex-wrap items-center gap-3">
        <Input
          aria-label="Buscar modelos"
          placeholder="Buscar modelos…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="w-64"
        />
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          Tipo
          <select
            aria-label="Filtrar por tipo"
            value={typeFilter}
            onChange={(e) => setTypeFilter(e.target.value as TemplateType | '')}
            className="rounded-md border border-border bg-surface-2 px-2 py-1 text-sm text-text-primary"
          >
            <option value="">Todos</option>
            {TEMPLATE_TYPES.map((type) => (
              <option key={type} value={type}>
                {type}
              </option>
            ))}
          </select>
        </label>
        <label className="flex items-center gap-2 text-sm text-text-secondary">
          Categoria
          <select
            aria-label="Filtrar por categoria"
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value as TemplateCategory | '')}
            className="rounded-md border border-border bg-surface-2 px-2 py-1 text-sm text-text-primary"
          >
            <option value="">Todas</option>
            {TEMPLATE_CATEGORIES.map((category) => (
              <option key={category} value={category}>
                {category}
              </option>
            ))}
          </select>
        </label>
      </div>

      <section aria-label="Modelos oficiais" className="mb-10">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-muted">Modelos oficiais</h2>
        {official.length === 0 ? (
          <p className="text-sm text-text-muted">Nenhum modelo oficial corresponde aos filtros.</p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {official.map((template) => (
              <TemplateCard key={template.id} template={template} onPreview={setPreviewing} onUse={setUsing} />
            ))}
          </div>
        )}
      </section>

      <section aria-label="Meus modelos">
        <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-text-muted">Meus modelos</h2>
        {custom.length === 0 ? (
          <p className="text-sm text-text-muted">
            Você ainda não salvou modelos personalizados. Abra um funil e use &quot;Salvar como modelo&quot; para
            criar um.
          </p>
        ) : (
          <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {custom.map((template) => (
              <TemplateCard
                key={template.id}
                template={template}
                onPreview={setPreviewing}
                onUse={setUsing}
                onEdit={setEditing}
                onUpdateFromFunnel={setUpdatingFrom}
                onDelete={setDeleting}
              />
            ))}
          </div>
        )}
      </section>

      <TemplatePreviewDialog
        template={previewing}
        onClose={() => setPreviewing(null)}
        onUse={(template) => {
          setPreviewing(null);
          setUsing(template);
        }}
      />
      <UseTemplateDialog template={using} onClose={() => setUsing(null)} />
      <EditCustomTemplateDialog template={editing} onClose={() => setEditing(null)} onSaved={load} />
      <UpdateFromFunnelDialog template={updatingFrom} onClose={() => setUpdatingFrom(null)} onUpdated={load} />
      <DeleteTemplateConfirmDialog
        template={deleting}
        onClose={() => setDeleting(null)}
        onConfirm={async () => {
          if (!deleting) return;
          await dashboardApi.deleteCustomTemplate(deleting.id);
          await load();
        }}
      />
    </main>
  );
}
